import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import config from '../../../config';
import {
  REPORT_ACCESS_CACHE_TTL,
  getCachedSessionData,
  setCachedSessionData,
} from '../reportCache';

const getCachedReportAccess = (cacheKey) => {
  const cached = getCachedSessionData(cacheKey, REPORT_ACCESS_CACHE_TTL);
  return Array.isArray(cached) ? cached : null;
};

// The embed token is kept (in sessionStorage: this tab only, cleared when the
// tab closes or on logout) until shortly before it expires, so a page refresh
// can start Power BI immediately instead of waiting for the token API.
// The original app kept it in localStorage for its whole life; this keeps the
// speed without writing it to disk. It stops being used 6 minutes before
// expiry, because the refresh timer (5 minutes before) must get a new token.
const TOKEN_EXPIRY_BUFFER_MS = 6 * 60 * 1000;

const getReportAccessTtl = (data) => {
  try {
    const firstToken = Object.values(data?.[0] || {})[0];
    const { exp } = jwtDecode(firstToken);
    return Math.max(exp * 1000 - Date.now() - TOKEN_EXPIRY_BUFFER_MS, 0);
  } catch (e) {
    return REPORT_ACCESS_CACHE_TTL;
  }
};

const setCachedReportAccess = (cacheKey, data) => {
  if (!Array.isArray(data) || data.length < 2) return; // don't cache failures
  if (data[2]?.partial) return; // some reports have no token: don't cache
  const ttl = getReportAccessTtl(data);
  if (ttl > 0) setCachedSessionData(cacheKey, data, ttl);
};

const getReportAccess = async (groupId, roleName, email, userId, options = {}) => {
  // force: skip the cached copy (used when a report is missing from it).
  // extraReportId: admin "view as" only, a report to include even if the
  // viewed user has no access to it.
  const { force = false, extraReportId = '' } = options;
  // IDs from the database can carry stray whitespace (e.g. a trailing tab).
  groupId = typeof groupId === 'string' ? groupId.trim() : groupId;
  email = typeof email === 'string' ? email.trim() : email;
  const token = localStorage.getItem('googleIdToken');
  const cacheKey = `onedata:report-access:${groupId || 'no-group'}:${roleName || 'no-role'}:${email || 'no-email'}:${userId || 'no-user'}:${extraReportId || 'no-extra'}`;

  const cachedResult = force ? null : getCachedReportAccess(cacheKey);
  if (cachedResult) {
    return cachedResult;
  }

  try {
    const result = await axios.get(
      `${config.apiURL}/getReportAccess?groupId=${encodeURIComponent(groupId)}&roleName=${encodeURIComponent(roleName)}&email=${encodeURIComponent(email)}${extraReportId ? `&extraReportId=${encodeURIComponent(extraReportId)}` : ''}`,
      {
        headers: {
          userId,
          Authorization: `Bearer ${token}`,
        },
      },
    );

    const normalized = Array.isArray(result?.data) ? result.data : [];
    setCachedReportAccess(cacheKey, normalized);
    return normalized;
  } catch (error) {
    console.error(
      '[getReportAccess] failed:',
      error?.response?.status,
      error?.response?.data || error?.message,
    );
    return [];
  }
};

// Tokens are per workspace: asks for each workspace and merges the replies
// into one [tokens, embedUrls] pair. A workspace that fails is left out (its
// reports then have no token); [] only if every workspace failed.
export const getReportAccessAll = async (
  groupIds,
  roleName,
  email,
  userId,
  options = {},
) => {
  const ids = [...new Set((groupIds || []).filter(Boolean))];
  const results = await Promise.all(
    ids.map((id) => getReportAccess(id, roleName, email, userId, options)),
  );
  const ok = results.filter((r) => Array.isArray(r) && r.length > 1);
  if (ok.length === 0) return [];
  return [
    Object.assign({}, ...ok.map((r) => r[0])),
    Object.assign({}, ...ok.map((r) => r[1])),
  ];
};

export default getReportAccess;
